import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import api from '../api/api';
import toast from 'react-hot-toast';
import { CURRENCY } from '../config/currency';

interface KOTData {
  kotNumber: string;
  orderNumber: number;
  orderTime: string;
  printTime: string;
  tableInfo: string;
  location: string;
  deliveryType: string;
  customerName: string;
  specialInstructions?: string; // Order-level special instructions
  allergyNote?: string; // Guest allergy declaration — highlighted on the printed KOT
  items: Array<{
    itemName: string;
    quantity: number;
    price: number;
    instructions: string[];
    total: number;
    requestedSpiceLevel?: string;
    spiceLevel?: string;
  }>;
  orderTotal: number;
  totalItems: number;
  totalQuantity: number;
  priority: string;
  waitTime: number;
  vendorName: string;
}

interface KOTPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: number | null;
  kitchenId?: number | null;
  autoPrint?: boolean;
}

const KOTPrintModal: React.FC<KOTPrintModalProps> = ({ isOpen, onClose, orderId, kitchenId, autoPrint = false }) => {
  const { theme } = useTheme();
  const [kotData, setKotData] = useState<KOTData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchKOTData = async () => {
    if (!orderId) return;
    
    let url = `/api/kitchen/orders/${orderId}/kot-template`;
    if (kitchenId) {
      url += `?kitchenId=${kitchenId}`;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(url);
      setKotData(response.data);
    } catch (err) {
      console.error('Error fetching KOT data:', err);
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to load KOT data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && orderId) {
      fetchKOTData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderId, kitchenId]);

  // Auto-print when KOT data is loaded and autoPrint is true
  useEffect(() => {
    if (autoPrint && kotData && !loading && !error) {
      console.log('[KOTPrintModal] Auto-printing KOT...');
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        handlePrint();
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrint, kotData, loading, error]);

  const handlePrint = () => {
    if (!kotData) return;
    
    // Try popup method first (better for receipt printers)
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      // Fallback to iframe method if popup is blocked
      toast('Using alternative print method...', { icon: 'ℹ️' });
      handleInlinePrint();
      return;
    }

    const printContent = generatePrintableKOT(kotData);
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Set up print event listeners
    printWindow.onafterprint = async () => {
      console.log('[KOTPrintModal] Print dialog closed');
      // Notify backend that KOT was printed
      if (kotData?.kotNumber) {
        try {
          // Extract KOT ID from kotNumber if available
          const kotIdMatch = kotData.kotNumber.match(/KOT-(\d+)-/);
          if (kotIdMatch && kotIdMatch[1]) {
            await api.put(`/api/kot/${kotIdMatch[1]}/status`, { status: 'PRINTED' });
            console.log('[KOTPrintModal] Marked KOT as printed');
          }
        } catch (error) {
          console.error('[KOTPrintModal] Failed to mark KOT as printed:', error);
        }
      }
      toast.success('KOT sent to printer');
      onClose();
    };
    
    // Trigger print
    printWindow.print();
  };

  // Alternative print method using iframe (doesn't trigger popup blocker)
  const handleInlinePrint = () => {
    if (!kotData) return;
    
    // Create a hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    iframe.style.width = '1px';
    iframe.style.height = '1px';
    document.body.appendChild(iframe);
    
    // Write content to iframe
    const printContent = generatePrintableKOT(kotData);
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.open();
      iframeDoc.write(printContent);
      iframeDoc.close();
      
      // Wait for content to load then print
      iframe.onload = () => {
        setTimeout(async () => {
          try {
            iframe.contentWindow?.print();
            
            // Mark as printed after a delay
            setTimeout(async () => {
              if (kotData?.kotNumber) {
                try {
                  const kotIdMatch = kotData.kotNumber.match(/KOT-(\d+)-/);
                  if (kotIdMatch && kotIdMatch[1]) {
                    await api.put(`/api/kot/${kotIdMatch[1]}/status`, { status: 'PRINTED' });
                    console.log('[KOTPrintModal] Marked KOT as printed');
                  }
                } catch (error) {
                  console.error('[KOTPrintModal] Failed to mark KOT as printed:', error);
                }
              }
              
              // Clean up iframe
              document.body.removeChild(iframe);
              toast.success('KOT sent to printer');
              onClose();
            }, 1000);
          } catch (error) {
            console.error('Print error:', error);
            toast.error('Failed to print KOT');
            document.body.removeChild(iframe);
          }
        }, 500);
      };
    }
  };

  const generatePrintableKOT = (data: KOTData): string => {
    const formatTime = (timeStr: string) => {
      return new Date(timeStr).toLocaleString();
    };

    const getPriorityColor = (priority: string) => {
      switch (priority) {
        case 'HIGH': return '#ff4444';
        case 'MEDIUM': return '#ff8800';
        default: return '#00aa00';
      }
    };

    const getSpiceLevelDisplay = (item: typeof data.items[0]) => {
      const spiceLevel = item.requestedSpiceLevel || item.spiceLevel;
      if (!spiceLevel || spiceLevel === 'NONE') return '';
      const displayName = spiceLevel.replace('_', ' ');
      return `<span style="font-size: 11px; margin-left: 5px;">🌶️ ${displayName}</span>`;
    };

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kitchen Order Ticket - ${data.kotNumber}</title>
          <style>
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
            
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              line-height: 1.4;
              margin: 0;
              padding: 10px;
              max-width: 300px;
            }
            
            .kot-header {
              text-align: center;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
              margin-bottom: 10px;
            }
            
            .kot-title {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            
            .priority {
              font-weight: bold;
              padding: 2px 6px;
              border-radius: 3px;
              color: white;
              background-color: ${getPriorityColor(data.priority)};
            }
            
            .info-section {
              margin-bottom: 10px;
              border-bottom: 1px dashed #666;
              padding-bottom: 8px;
            }
            
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 3px;
            }
            
            .items-section {
              margin-bottom: 10px;
            }
            
            .item {
              margin-bottom: 8px;
              border-bottom: 1px dotted #999;
              padding-bottom: 5px;
            }
            
            .item-header {
              font-weight: bold;
              font-size: 14px;
            }
            
            .item-qty {
              float: right;
              font-weight: bold;
            }
            
            .instructions {
              font-style: italic;
              color: #666;
              margin-top: 3px;
              font-size: 11px;
            }
            
            .footer {
              text-align: center;
              border-top: 2px solid #000;
              padding-top: 10px;
              margin-top: 10px;
            }
            
            .total-summary {
              font-weight: bold;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <div class="kot-header">
            <div class="kot-title">KITCHEN ORDER TICKET</div>
            <div>KOT: ${data.kotNumber}</div>
            <div class="priority">${data.priority} PRIORITY</div>
          </div>
          
          <div class="info-section">
            <div class="info-row">
              <span><strong>Order #:</strong> ${data.orderNumber}</span>
              <span><strong>Wait:</strong> ${data.waitTime}min</span>
            </div>
            <div class="info-row">
              <span><strong>Table:</strong> ${data.tableInfo}</span>
              <span><strong>Type:</strong> ${data.deliveryType}</span>
            </div>
            <div class="info-row">
              <span><strong>Customer:</strong> ${data.customerName}</span>
            </div>
            <div class="info-row">
              <span><strong>Time:</strong> ${formatTime(data.orderTime)}</span>
            </div>
          </div>

          ${data.allergyNote ? `
          <div style="border: 3px solid #000; padding: 8px; margin-bottom: 10px; font-weight: bold;">
            *** ALLERGY — READ BEFORE PREPARING ***<br>
            ${data.allergyNote}
          </div>
          ` : ''}

          <div class="items-section">
            <div style="font-weight: bold; margin-bottom: 8px;">ITEMS TO PREPARE:</div>
            ${data.items.map(item => `
              <div class="item">
                <div class="item-header">
                  ${item.itemName}
                  ${getSpiceLevelDisplay(item)}
                  <span class="item-qty">x${item.quantity}</span>
                </div>
                ${item.instructions.length > 0 ? `
                  <div class="instructions">
                    ${item.instructions.join('<br>')}
                  </div>
                ` : ''}
              </div>
            `).join('')}
          </div>

          ${data.specialInstructions ? `
          <div class="info-section" style="background-color: #fff3cd; padding: 8px; border-radius: 4px; margin-bottom: 10px;">
            <div style="font-weight: bold; margin-bottom: 4px;">⚠️ SPECIAL INSTRUCTIONS:</div>
            <div style="font-style: italic;">${data.specialInstructions}</div>
          </div>
          ` : ''}
          
          <div class="footer">
            <div class="total-summary">
              Total Items: ${data.totalQuantity} | Amount: ${CURRENCY.symbol}${data.orderTotal.toFixed(2)}
            </div>
            <div style="margin-top: 5px; font-size: 10px;">
              Printed: ${formatTime(data.printTime)}
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const handleGenerateKOT = async () => {
    if (!orderId) return;
    
    try {
      await api.post(`/kitchen/orders/${orderId}/kot`);
      toast.success('KOT generated successfully');
      await fetchKOTData(); // Refresh data
    } catch (err) {
      console.error('Error generating KOT:', err);
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to generate KOT');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: theme.components.card.background }}
      >
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center" style={{ borderColor: theme.components?.card?.border || '#E8E6E1' }}>
          <h2 className="text-xl font-bold" style={{ color: theme.primary }}>
            Kitchen Order Ticket
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full transition-colors"
            style={{
              backgroundColor: theme.button.secondary.bg,
              color: theme.button.secondary.text,
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" 
                   style={{ borderColor: theme.primary }}></div>
              <p style={{ color: theme.text }}>Loading KOT data...</p>
            </div>
          )}

          {error && (
            <div className="px-4 py-3 rounded mb-4" style={{ backgroundColor: `${theme.danger}15`, border: `1px solid ${theme.danger}40`, color: theme.danger }}>
              {error}
            </div>
          )}

          {kotData && !loading && !error && (
            <div>
              {/* KOT Preview */}
              <div className="border rounded-lg p-4 mb-6" 
                   style={{ backgroundColor: theme.background, borderColor: theme.components.card.border }}>
                <div className="text-center mb-4">
                  <h3 className="font-bold text-lg" style={{ color: theme.primary }}>
                    KITCHEN ORDER TICKET
                  </h3>
                  <p className="text-sm" style={{ color: theme.gray[600] }}>
                    {kotData.kotNumber}
                  </p>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${
                    kotData.priority === 'HIGH' ? 'bg-red-500 text-white' :
                    kotData.priority === 'MEDIUM' ? 'bg-yellow-500 text-white' :
                    'bg-green-500 text-white'
                  }`}>
                    {kotData.priority} PRIORITY
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm mb-4" style={{ color: theme.text }}>
                  <div><strong>Order #:</strong> {kotData.orderNumber}</div>
                  <div><strong>Wait Time:</strong> {kotData.waitTime} min</div>
                  <div><strong>Table:</strong> {kotData.tableInfo}</div>
                  <div><strong>Type:</strong> {kotData.deliveryType}</div>
                  <div className="col-span-2"><strong>Customer:</strong> {kotData.customerName}</div>
                </div>

                <div className="mb-4">
                  <h4 className="font-bold mb-2" style={{ color: theme.primary }}>Items to Prepare:</h4>
                  {kotData.items.map((item, index) => {
                    const spiceLevel = item.requestedSpiceLevel || item.spiceLevel;
                    return (
                      <div key={index} className="mb-3 pb-2 border-b">
                        <div className="flex justify-between items-start">
                          <div className="flex flex-col gap-1">
                            <span className="font-medium">{item.itemName}</span>
                            {spiceLevel && spiceLevel !== 'NONE' && (
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                spiceLevel === 'MILD' ? 'bg-green-100 text-green-800' :
                                spiceLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                                spiceLevel === 'HOT' ? 'bg-orange-100 text-orange-800' :
                                spiceLevel === 'EXTRA_HOT' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                🌶️ {spiceLevel.replace('_', ' ')}
                              </span>
                            )}
                          </div>
                          <span className="font-bold">x{item.quantity}</span>
                        </div>
                        {item.instructions.length > 0 && (
                          <div className="text-xs italic mt-1" style={{ color: theme.gray[600] }}>
                            {item.instructions.join(', ')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Allergy declaration — always highlighted for the kitchen */}
                {kotData.allergyNote && (
                  <div className="mb-4 p-3 rounded-lg bg-red-50 border-2 border-red-400">
                    <h4 className="font-bold text-sm text-red-800 mb-1 uppercase">Allergy — read before preparing:</h4>
                    <p className="text-sm font-semibold text-red-700">{kotData.allergyNote}</p>
                  </div>
                )}

                {/* Order-level Special Instructions */}
                {kotData.specialInstructions && (
                  <div className="mb-4 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                    <h4 className="font-bold text-sm text-yellow-800 mb-1">Special Instructions:</h4>
                    <p className="text-sm italic text-yellow-700">{kotData.specialInstructions}</p>
                  </div>
                )}

                <div className="text-center pt-2 border-t">
                  <div className="font-bold">
                    Total Items: {kotData.totalQuantity} | Amount: {CURRENCY.symbol}{kotData.orderTotal.toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handlePrint}
                  className="flex-1 py-3 px-4 rounded-lg font-medium transition-colors"
                  style={{
                    backgroundColor: theme.button.primary.bg,
                    color: theme.button.primary.text,
                  }}
                >
                  🖨️ Print KOT
                </button>
                <button
                  onClick={handleGenerateKOT}
                  className="flex-1 py-3 px-4 rounded-lg font-medium transition-colors"
                  style={{
                    backgroundColor: theme.button.secondary.bg,
                    color: theme.button.secondary.text,
                  }}
                >
                  🔄 Regenerate
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KOTPrintModal; 